import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useNightSurface } from "@/lib/mode";
import { ArrowRight, Lock, Ban, Gift, Smartphone, Mail, Zap, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { PhoneMockup, FinalCta } from "@/components/LandingSections";
import { Simulator, ChallengeTypes, Features } from "@/components/LandingExplain";
import { SocialLinks, SOCIALS } from "@/components/Socials";
import { HeroBubbles } from "@/components/HeroBubbles";

const STEPS = [
  { icon: Mail, title: "Reçois une invitation", text: "Un ami t'envoie le lien de sa ligue privée. Une minute pour créer ton compte." },
  { icon: Zap, title: "Fais tes pronos", text: "Choisis les vainqueurs des matchs NBA du soir en quelques appuis." },
  { icon: MessageCircle, title: "Chambre tes potes", text: "Classement, rivalités, défis : le dernier relève le défi choisi par la ligue." },
];

const BANTER = ["« Tu vas finir dernier, comme d'hab. »", "« 5 de suite, qui dit mieux ? »", "« Les croissants lundi, c'est pour toi. »", "« Revanche ? »",
  "« Personne n'y croyait, et pourtant… »", "« Le resto, c'est toi qui organises. »", "« Je te rattrape ce soir. »", "« Match vedette : je double la mise de points. »"];

const FAQ = [
  ["Qui peut créer une ligue ?", "Tout le monde. Une fois inscrit grâce à une invitation, tu peux créer ta propre ligue et devenir son organisateur."],
  ["Est-ce que ça coûte quelque chose ?", "Non. Aucune mise, aucune cote, aucun paiement : seulement des points et des défis acceptés librement."],
  ["Et si je rate un soir ?", "Aucun point perdu et ta série n'est pas cassée. Un rappel sur ton téléphone t'évite d'oublier."],
  ["Qui choisit les règles de points ?", "L'organisateur, à la création de la ligue (Détente, Compétition, Expert ou personnalisé). Elles sont figées dès le début de la compétition : personne ne les change en cours de route."],
  ["Jusqu'à quand puis-je faire mon prono ?", "Jusqu'au coup d'envoi de chaque match. Ensuite, le prono est verrouillé."],
];

export function Banter() {
  const items = [...BANTER, ...BANTER];
  return (
    <div className="relative z-10 overflow-hidden border-y border-white/[0.06] py-4" data-testid="landing-banter" aria-hidden="true">
      <div className="mpl-marquee flex w-max gap-8 whitespace-nowrap">{items.map((t, i) => (
        <span key={i} className="font-display text-2xl font-extrabold uppercase text-white/70"><span className="mr-8 inline-block h-2.5 w-2.5 rounded-full bg-[#B7F52A] align-middle" aria-hidden="true" />{t}</span>))}</div>
    </div>
  );
}

export function Steps() {
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-5 py-16" data-testid="landing-steps">
      <p className="mpl-eyebrow text-[#B7F52A]">Comment ça marche</p>
      <h2 className="mt-2 font-display text-4xl font-extrabold uppercase sm:text-5xl">3 étapes. 30 secondes par soir.</h2>
      <ol className="mt-8 grid gap-3 md:grid-cols-3">{STEPS.map((s, i) => (
        <li key={s.title} className="mpl-card mpl-rise group relative overflow-hidden p-6 transition-transform duration-300 hover:-translate-y-1" style={{ animationDelay: `${150 + i * 120}ms` }} data-testid={`landing-step-${i + 1}`}>
          <span className="absolute -right-2 -top-4 font-display text-8xl font-extrabold text-white/[0.05] transition-colors duration-300 group-hover:text-[#B7F52A]/10">{i + 1}</span>
          <s.icon className="text-[#B7F52A]" size={24} />
          <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
          <p className="mt-1 text-sm text-slate-400">{s.text}</p>
        </li>))}</ol>
    </section>
  );
}

function Faq() {
  return (
    <section className="relative z-10 mx-auto max-w-3xl px-5 pb-16" data-testid="landing-faq">
      {FAQ.map(([q, a], i) => (
        <details key={q} className="group border-b border-white/[0.08] py-4" data-testid={`landing-faq-${i + 1}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-bold">{q}<span className="text-[#B7F52A] transition-transform duration-200 group-open:rotate-45">+</span></summary>
          <p className="mt-2 text-sm text-slate-400">{a}</p>
        </details>))}
    </section>
  );
}

export default function Landing() {
  useNightSurface();
  const [link, setLink] = useState("");
  const nav = useNavigate();
  const go = (e) => {
    e.preventDefault();
    const m = link.trim().match(/invitation\/([A-Za-z0-9_-]+)/) || link.trim().match(/^([A-Za-z0-9_-]{16,})$/);
    if (m) nav(`/invitation/${m[1]}`);
    else toast.error("Ce lien d'invitation n'est pas valide. Copie le lien complet reçu de l'organisateur.");
  };
  return (
    <div className="mpl-grain min-h-screen overflow-x-hidden bg-[#0B1020] text-white">
      <div className="relative" data-testid="landing-hero">
      <div className="mpl-photo mpl-photo-dunk" aria-hidden="true" data-testid="landing-hero-photo" />
      <span className="mpl-photo-caption">Illustration</span>
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-5 py-5" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}>
        <Link to="/" aria-label="Accueil Ma Petite Ligue" data-testid="landing-home-link"><Logo /></Link>
        <nav className="flex items-center gap-2">
          <Link to="/ligue-a-ton-image" className="hidden whitespace-nowrap rounded-full border border-[#B7F52A]/40 px-4 py-2 text-sm font-bold text-[#B7F52A] transition-colors hover:bg-[#B7F52A]/10 sm:inline-flex" data-testid="landing-branded-tab">Ligue à ton image</Link>
          <Link to="/connexion" className="mpl-btn-ghost" data-testid="landing-login-button">Connexion</Link>
        </nav>
      </header>
      <div className="relative z-10 px-5 sm:hidden">
        <Link to="/ligue-a-ton-image" className="inline-flex items-center gap-1.5 rounded-full border border-[#B7F52A]/40 bg-black/30 px-3 py-1.5 text-xs font-bold text-[#B7F52A] backdrop-blur" data-testid="landing-branded-tab-mobile">Entreprises & communautés : ta ligue à ton image <ArrowRight size={13} /></Link>
      </div>
      <section className="relative z-10 mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 pt-8 md:grid-cols-[1.2fr_1fr] md:pt-16">
        <div className="relative z-[1]">
          <p className="mpl-eyebrow mpl-rise mb-4 text-[#B7F52A]">Les matchs passent. Les rivalités restent.</p>
          <h1 className="mpl-rise font-display text-5xl font-extrabold uppercase leading-[0.92] sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }} data-testid="landing-title">
            Tes pronos NBA <span className="text-[#B7F52A]">contre ta bande.</span>
          </h1>
          <p className="mpl-rise mt-6 max-w-md text-base text-slate-300" style={{ animationDelay: "160ms" }} data-testid="landing-pitch">
            Chaque soir, choisis les vainqueurs. À la fin de la période, le premier savoure… et le dernier relève le défi de la ligue.
          </p>
          <form id="invitation" onSubmit={go} className="mpl-rise mt-8 flex max-w-md scroll-mt-24 flex-col gap-2 sm:flex-row" style={{ animationDelay: "240ms" }} data-testid="landing-invite-form">
            <input className="mpl-input" placeholder="Colle ton lien d'invitation" value={link} onChange={(e) => setLink(e.target.value)} data-testid="landing-invite-input" />
            <button className="mpl-btn-primary shrink-0" data-testid="landing-invite-submit">J'ai une invitation <ArrowRight size={16} /></button>
          </form>
          <a href="#simulateur" className="mpl-rise mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#B7F52A] hover:underline" style={{ animationDelay: "280ms" }} data-testid="landing-sim-link">Essayer le simulateur de points ↓</a>
          <ul className="mpl-rise mt-5 flex flex-wrap gap-2 text-xs" style={{ animationDelay: "320ms" }} data-testid="landing-badges">
            {[[Gift, "Gratuit"], [Lock, "Sur invitation"], [Ban, "Aucun pari d'argent"], [Smartphone, "Sur ton téléphone"]].map(([I, t]) => (
              <li key={t} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-slate-200"><I size={13} className="text-[#B7F52A]" /> {t}</li>))}
          </ul>
          <p className="mt-4 flex items-center gap-2 text-xs text-slate-500" data-testid="landing-invite-only"><Lock size={13} /> Ma Petite Ligue fonctionne exclusivement sur invitation.</p>
        </div>
        <div className="relative z-[1] flex justify-center md:justify-end">
          <div className="mpl-float"><PhoneMockup /></div>
          <HeroBubbles />
        </div>
      </section>
      </div>
      <Banter />
      <Steps />
      <Simulator />
      <ChallengeTypes />
      <Features />
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-16">
        <div className="flex items-start gap-3 rounded-2xl border border-white/[0.07] p-5 text-sm text-slate-300" data-testid="landing-friendly">
          <Ban className="shrink-0 text-[#B7F52A]" size={20} />
          <p><span className="font-semibold text-white">100 % amical.</span> Les défis (croissants, resto, maillot rival…) sont choisis par la ligue, acceptés par chacun, et une option sans dépense est toujours possible. <Sparkles size={13} className="inline text-[#B7F52A]" /></p>
        </div>
      </section>
      <Faq />
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-16" data-testid="landing-socials">
        <p className="mpl-eyebrow mb-2">Suis-nous</p>
        <h2 className="mb-2 font-display text-3xl font-extrabold uppercase sm:text-4xl">Rejoins la communauté @mapetiteligue</h2>
        <p className="mb-5 max-w-xl text-sm text-slate-400">Classements de la semaine, défis les plus fous, nouveautés de l'app : tout passe par nos réseaux.</p>
        <div className="max-w-2xl"><SocialLinks testPrefix="landing-follow" /></div>
      </section>
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-16" data-testid="landing-branded-teaser">
        <div className="mpl-card flex flex-col items-start gap-4 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="mpl-eyebrow mb-1 text-[#B7F52A]">Entreprises, communautés, créateurs</p>
            <p className="font-display text-2xl font-extrabold uppercase">Une ligue et une app à ton image</p>
            <p className="mt-1 max-w-lg text-sm text-slate-400">Ton logo, tes couleurs, tes lots : fais vivre ton équipe ou ta communauté autour de la NBA.</p>
          </div>
          <Link to="/ligue-a-ton-image" className="mpl-btn-primary shrink-0" data-testid="landing-branded-cta">Découvrir <ArrowRight size={16} /></Link>
        </div>
      </section>
      <FinalCta />
      <footer className="relative z-10 mx-auto flex max-w-6xl flex-wrap gap-4 px-5 pb-10 text-xs text-slate-500">
        <span>© {new Date().getFullYear()} Ma Petite Ligue</span>
        <Link to="/confidentialite" className="hover:text-white" data-testid="footer-privacy-link">Confidentialité</Link>
        <Link to="/conditions" className="hover:text-white" data-testid="footer-terms-link">Conditions d'utilisation</Link>
        <span>Non affilié à la NBA.</span>
        <span className="ml-auto flex gap-1">{SOCIALS.map(({ key, label, url, Icon }) => (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={`${label} @mapetiteligue`} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white/5 hover:text-[#B7F52A]" data-testid={`footer-${key}-link`}><Icon size={16} /></a>))}</span>
      </footer>
    </div>
  );
}
